import { read as readXlsx, utils as xlsxUtils } from 'xlsx'
import mammoth from 'mammoth'

/**
 * Client-side ingestion helpers for the AI Generator popover.
 *
 * Everything here runs locally in the browser — spreadsheets and documents
 * are parsed on-device and normalized into flat plaintext before being handed
 * to Gemini. Nothing leaves the device until the user clicks Generate, which
 * keeps the "drop is free, only Generate burns credits" billing model honest.
 *
 * Extracted text is capped at MAX_EXTRACT_CHARS per source so a large workbook
 * can't blow the model's token budget; truncated payloads get an explicit
 * marker so the model knows the tail was dropped.
 */
export const MAX_EXTRACT_CHARS = 24_000

const capExtractedText = (text: string) => {
  const trimmed = text.trim()
  if (trimmed.length <= MAX_EXTRACT_CHARS) return trimmed
  return `${trimmed.slice(0, MAX_EXTRACT_CHARS)}\n\n[… truncated ${(
    trimmed.length - MAX_EXTRACT_CHARS
  ).toLocaleString()} more characters not sent to the model …]`
}

const readFileAsArrayBuffer = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => resolve(event.target?.result as ArrayBuffer)
    reader.onerror = () => reject(new Error('Failed reading file data stream.'))
    reader.readAsArrayBuffer(file)
  })

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (event) => resolve((event.target?.result as string) ?? '')
    reader.onerror = () => reject(new Error('Failed reading text stream data.'))
    reader.readAsText(file)
  })

/**
 * Parses binary spreadsheet files (.xlsx, .xls, .csv) into flat CSV text, one
 * labelled block per sheet.
 */
export const extractSpreadsheetText = async (file: File): Promise<string> => {
  try {
    const data = new Uint8Array(await readFileAsArrayBuffer(file))
    const workbook = readXlsx(data, { type: 'array' })
    let compiled = ''

    workbook.SheetNames.forEach((sheetName) => {
      const worksheet = workbook.Sheets[sheetName]
      compiled += `\n--- Sheet Table: ${sheetName} ---\n`
      compiled += `${xlsxUtils.sheet_to_csv(worksheet)}\n`
    })

    const capped = capExtractedText(compiled)
    if (!capped) throw new Error('That spreadsheet had no readable cells.')
    return capped
  } catch (error) {
    if (error instanceof Error && error.message.includes('no readable cells')) throw error
    throw new Error('Failed to parse the spreadsheet grid structure.', { cause: error })
  }
}

/**
 * Extracts raw text from document files (.docx, .txt, .md). Word documents go
 * through mammoth; plaintext and Markdown are read directly.
 */
export const extractDocumentText = async (file: File): Promise<string> => {
  const extension = file.name.split('.').pop()?.toLowerCase()

  if (extension === 'pdf') {
    try {
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs')
      pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString()
      const document = await pdfjs.getDocument({ data: new Uint8Array(await readFileAsArrayBuffer(file)) }).promise
      const pages: string[] = []
      for (let pageNumber = 1; pageNumber <= document.numPages; pageNumber += 1) {
        const page = await document.getPage(pageNumber)
        const content = await page.getTextContent()
        pages.push(content.items.map((item) => 'str' in item ? item.str : '').join(' '))
      }
      const capped = capExtractedText(pages.join('\n\n'))
      if (!capped) throw new Error('That PDF had no readable text.')
      return capped
    } catch (error) {
      if (error instanceof Error && error.message.includes('no readable text')) throw error
      throw new Error('Failed to read the PDF document.', { cause: error })
    }
  }

  if (extension === 'docx') {
    try {
      const arrayBuffer = await readFileAsArrayBuffer(file)
      const result = await mammoth.extractRawText({ arrayBuffer })
      const capped = capExtractedText(result.value)
      if (!capped) throw new Error('That document had no readable text.')
      return capped
    } catch (error) {
      if (error instanceof Error && error.message.includes('no readable text')) throw error
      throw new Error('Failed to read the Word document.', { cause: error })
    }
  }

  const capped = capExtractedText(await readFileAsText(file))
  if (!capped) throw new Error('That file had no readable text.')
  return capped
}

/**
 * Pulls a public Google Sheet down as CSV via its export endpoint.
 *
 * This is a direct client fetch, so it only succeeds for sheets shared as
 * "Anyone with the link can view". Private or restricted sheets trip the
 * browser CORS / auth wall and reject — the popover catches that and shows the
 * "CORS Lock Detected" recovery banner. A serverless proxy fallback is
 * deferred to a later phase.
 */
export const extractGoogleSheetText = async (url: string): Promise<string> => {
  const idMatch = url.match(/\/d\/([a-zA-Z0-9-_]+)/)
  if (!idMatch?.[1]) {
    throw new Error('That does not look like a Google Sheets link.')
  }

  const exportUrl = `https://docs.google.com/spreadsheets/d/${idMatch[1]}/export?format=csv`
  const response = await fetch(exportUrl)
  if (!response.ok) {
    throw new Error('Google rejected the request — the sheet is not publicly viewable.')
  }

  const capped = capExtractedText(await response.text())
  if (!capped) throw new Error('The linked sheet returned no rows.')
  return capped
}
